import React, { useEffect, useState } from 'react';
import { auth } from '../firebaseConfig';
import styles from './UsageAnalytics.module.scss';
import axios from 'axios';
import AspectHeatmap from "../components/AspectHeatmap";
import FeatureGate from '../components/FeatureGate';

interface UsageData {
  reviewsGenerated: number;
  qrCodesCreated: number;
  alertsReceived: number;
  locationsManaged: number;
  apiCalls: number;
  storageUsed: number;       // MB
  lastActivity: string | null;
}

interface PlanLimits {
  [key: string]: {
    reviews: number;
    qrCodes: number;
    alerts: number;
    locations: number;
    storage: number;         // MB
  };
}

interface Review {
  id?: string;
  content: string;
  rating: number;
  date?: string;
  platform?: string;
  author?: string;
}

interface Reply {
  createdAt: string;
}

const planLimits: PlanLimits = {
  trial: { reviews: 50, qrCodes: 5, alerts: 10, locations: 1, storage: 100 },
  basic: { reviews: 500, qrCodes: 20, alerts: 50, locations: 3, storage: 500 },
  pro: { reviews: 2000, qrCodes: 100, alerts: 200, locations: 10, storage: 2000 },
};

const API = process.env.REACT_APP_API_URL || '/api';

/** Safely fetch JSON (won’t throw on 404/HTML). Returns null if not JSON or not ok. */
async function safeFetchJSON(url: string, token: string) {
  try {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      await res.text().catch(() => undefined);
      return null;
    }
    const ct = res.headers.get('content-type') || '';
    if (!ct.toLowerCase().includes('application/json')) {
      await res.text().catch(() => undefined);
      return null;
    }
    return await res.json();
  } catch {
    return null;
  }
}

/** Tenant-scoped reviews (uses /api/my-business-reviews) */
async function fetchMyReviews(token: string): Promise<Review[]> {
  try {
    const res = await fetch(`${API}/my-business-reviews`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return [];
    const data = await res.json();
    if (!Array.isArray(data)) return [];
    // Normalize to our Review interface
    return data.map((r: any) => ({
      id: r._id || r.id,
      content: r.content,
      rating: Number(r.rating) || 0,
      date: r.date ? new Date(r.date).toISOString() : undefined,
      author: r.author,
      platform: r.platform,
    })) as Review[];
  } catch {
    return [];
  }
}

const UsageAnalytics: React.FC = () => {
  const [usageData, setUsageData] = useState<UsageData | null>(null);
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [replies, setReplies] = useState<Reply[]>([]);
  const [loading, setLoading] = useState(true);
  const [warning, setWarning] = useState<string | null>(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          setWarning('Non connecté(e).');
          return;
        }
        const token = await user.getIdToken();

        const [subJSON, usageJSON, myReviews, repliesRes] = await Promise.all([
          safeFetchJSON(`${API}/subscription/status`, token),
          safeFetchJSON(`${API}/usage/analytics`, token),
          fetchMyReviews(token),
          (async () => {
            try {
              const r = await axios.get(`${API}/ai/history`, { headers: { Authorization: `Bearer ${token}` } });
              return Array.isArray(r.data?.history) ? (r.data.history as Reply[]) : [];
            } catch {
              return [];
            }
          })(),
        ]);

        // plan
        setSubscriptionData(subJSON?.subscription ?? { plan: 'trial', status: 'inactive' });

        // reviews + replies
        setReviews(myReviews);
        setReplies(repliesRes);

        // fallback usage if endpoint missing
        const localQRCodes = (() => {
          try {
            const raw = localStorage.getItem('savedQRCodes');
            const arr = raw ? JSON.parse(raw) : [];
            return Array.isArray(arr) ? arr.length : 0;
          } catch { return 0; }
        })();

        const nowISO = new Date().toISOString();
        const fallbackUsage: UsageData = {
          reviewsGenerated: myReviews.length, // tenant-scoped
          qrCodesCreated: localQRCodes,
          alertsReceived: 0,
          locationsManaged: 0,
          apiCalls: repliesRes.length,
          storageUsed: 0,
          lastActivity: (myReviews[0]?.date ?? repliesRes[0]?.createdAt ?? nowISO),
        };

        // Always override reviewsGenerated with tenant count (defensive)
        if (usageJSON?.usage) {
          setUsageData({ ...usageJSON.usage, reviewsGenerated: myReviews.length });
        } else {
          setUsageData(fallbackUsage);
          setWarning('Certaines métriques sont estimées (endpoint /usage/analytics manquant).');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  if (loading || !usageData) {
    return (
      <div className={styles.container}>
        <p>Chargement des données...</p>
      </div>
    );
  }

  // ---------- gating info ----------
  const currentPlan = String(subscriptionData?.plan || 'trial').toLowerCase();
  const currentStatus = String(subscriptionData?.status || '');
  // ----------------------------------

  const totalReviews = reviews.length;
  const percentReplied = totalReviews ? Math.round((replies.length / totalReviews) * 100) : 0;
  const limits = planLimits[currentPlan as keyof typeof planLimits] || planLimits.trial;

  const pct = (used: number, limit: number) => Math.min((used / limit) * 100, 100);
  const fmtStorage = (mb: number) => (mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`);
  const barColor = (p: number) => (p >= 90 ? styles.critical : p >= 75 ? styles.warning : styles.normal);

  const usageList = [
    { key: 'reviews', label: '📝 Avis Générés', used: usageData.reviewsGenerated, limit: limits.reviews },
    { key: 'qr', label: '📱 QR Codes Créés', used: usageData.qrCodesCreated, limit: limits.qrCodes },
    { key: 'alerts', label: '🔔 Alertes Reçues', used: usageData.alertsReceived, limit: limits.alerts },
    { key: 'locations', label: '📍 Emplacements Gérés', used: usageData.locationsManaged, limit: limits.locations },
    { key: 'storage', label: '💾 Stockage Utilisé', used: usageData.storageUsed, limit: limits.storage, formatted: true as const },
    { key: 'api', label: '🌐 Appels API', used: usageData.apiCalls, limit: 1000 },
  ];

  // ------- PAGE RENDER (gated) -------
  return (
    <main className={styles.container}>
      <FeatureGate
        requiredPlan="pro"
        currentPlan={currentPlan}
        requireActive
        currentStatus={currentStatus}
        featureName="Analytics Avancées"
        featureDescription="Accédez à vos métriques d’usage détaillées et au nuage de mots."
      >
        <div className={styles.header}>
          <h1>Analytics d&apos;Utilisation</h1>
          <p>Suivi de votre plan actuel et de votre activité</p>
        </div>

        {warning && <div className={styles.warningBanner}>{warning}</div>}

        <div className={styles.planInfo}>
          <div className={styles.planCard}>
            <h3>Plan Actuel: {subscriptionData?.plan || 'Trial'}</h3>
            <p>
              Dernière activité{' '}
              {usageData.lastActivity ? new Date(usageData.lastActivity).toLocaleDateString('fr-FR') : '—'}
            </p>
          </div>
        </div>

        <div className={styles.analyticsSection}>
          <h2>📊 Engagement & Réponses</h2>
          <div className={styles.analyticsGrid}>
            <div className={styles.analyticsCard}>
              <h4>Total Avis</h4>
              <p><strong>{totalReviews}</strong></p>
            </div>

            <div className={styles.analyticsCard}>
              <h4>% d&apos;Avis Répondus</h4>
              <p><strong>{percentReplied}%</strong></p>
              <p>{replies.length} réponses / {totalReviews} avis</p>
            </div>

            <div className={styles.analyticsCard}>
              <h4>Thèmes & sentiment</h4>
              <div style={{ minHeight: 120 }}>
                {reviews?.length ? (
                  <AspectHeatmap reviews={reviews} variant="bare" compact />
                ) : (
                  <span style={{ opacity: 0.7 }}>Pas assez de données pour les thèmes.</span>
                )}
              </div>
            </div>

          </div>
        </div>

        <div className={styles.metricsGrid}>
          {usageList.map(m => {
            const percentage = pct(m.used, m.limit);
            return (
              <div key={m.key} className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <h3>{m.label}</h3>
                  <span className={styles.metricValue}>
                    {'formatted' in m && m.formatted
                      ? `${fmtStorage(m.used)} / ${fmtStorage(m.limit)}`
                      : `${m.used} / ${m.limit}`}
                  </span>
                </div>
                <div className={styles.progressBar}>
                  <div
                    className={`${styles.progressFill} ${barColor(percentage)}`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                <p className={styles.metricDescription}>
                  {'formatted' in m && m.formatted
                    ? m.used >= m.limit
                      ? `Limite dépassée de ${fmtStorage(m.used - m.limit)}`
                      : `${fmtStorage(m.limit - m.used)} restants`
                    : m.used >= m.limit
                      ? `Limite dépassée de ${m.used - m.limit}`
                      : `${m.limit - m.used} restants`}
                </p>
              </div>
            );
          })}
        </div>
      </FeatureGate>
    </main>
  );
};

export default UsageAnalytics;
